<template>
  <sc-render
    :fields="fields"
    :emptyMessage="$t('NO_DATA_SOURCE_EXIST')"
  >
    <div
      class="image-landscape"
      :class="[textPosition, { 'dark-theme' : isDarkTheme }]"
    >
      <div
        class="image-container"
        :class="{ clickable: hasLinkHref }"
        @click="handleImageClick"
      >
        <ShImage
          v-if="!isEditMode && image"
          :src="imageSrc"
          :alt="image.value.alt"
        />
        <sc-image
          v-else
          :media="image"
        />
        <div
          v-if="showCardDescription"
          class="card-description"
          :class="{ 'dark-theme' : isDarkTheme }"
        >
          <sc-text
            v-if="isEditMode || hasTitleValue"
            class="title"
            tag="h2"
            :field="title"
          />
          <sc-text
            v-if="isEditMode || hasDescriptionValue"
            class="description"
            tag="div"
            :field="description"
          />
        </div>
      </div>
      <sc-link
        v-if="isEditMode"
        class="sc-link"
        :field="link"
      />
      <sh-button
        v-if="!isEditMode && hasLinkHref"
        ref="ctaButton"
        class="cta"
        :[btnLinkKey]="link.value.href"
        :target="link.value.target"
        :naked="true"
      >
        {{ link.value.text }}
      </sh-button>
    </div>
  </sc-render>
</template>

<script>

import { Image, Text, Link } from '@sitecore-jss/sitecore-jss-vue'
import { createNamespacedHelpers } from 'vuex'
import { getButtonType } from '~/helpers/get-button-type'
import get from '~/helpers/get'
import ScRender from '../sc-render/'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')
const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

export default {
  name: 'LandscapeImage',

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  components: {
    ScImage: Image,
    ScRender,
    ScText: Text,
    ScLink: Link
  },

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapEnvGetters(['isDarkTheme']),

    textPosition () {
      return get(this.fields, 'textPosition.value', '').toLowerCase()
    },

    link () {
      return get(this.fields, 'link', false)
    },

    hasLinkHref () {
      return !!(this.link && this.link.value.href)
    },

    image () {
      return get(this.fields, 'image', false)
    },

    imageSrc () {
      return this.image.value.cloudinary_4x3_fill_crop || this.image.value.src
    },

    title () {
      return get(this.fields, 'title', false)
    },

    hasTitleValue () {
      return this.title && this.title.value
    },

    description () {
      return get(this.fields, 'description', false)
    },

    hasDescriptionValue () {
      return this.description && this.description.value
    },

    showCardDescription () {
      return this.isEditMode || this.hasTitleValue || this.hasDescriptionValue || this.hasLinkHref
    },

    btnLinkKey () {
      if (!this.hasLinkHref) return ''

      return getButtonType(this.link.value.href)
    }
  },

  methods: {
    handleImageClick () {
      if (!this.$refs.ctaButton) return

      this.$refs.ctaButton.$el.click()
    }
  }
}
</script>

<style lang="scss" scoped>

.image-landscape {
  margin: 0 auto $space-090;

  @include tablet {
    max-width: 55%;
    margin: 0 auto $space-110;
  }

  .image-container {
    &.clickable {
      cursor: pointer;
    }

    ::v-deep .sc-image-wrapper img {
      width: 100%;
      height: auto;
    }
  }

  &.center {
    text-align: center;
  }

  .sc-link {
    @include font('040');

    font-weight: 700;
    text-decoration: none;
  }

  .card-description {
    margin-top: $space-060;
    padding: 0 $space-050;

    @include tablet {
      margin-top: $space-040;
      padding: unset;
    }
  }

  .title {
    @include font('060');

    margin-bottom: $space-020;
  }

  .description {
    @include font('040');

    margin-bottom: $space-020;
  }

  .cta {
    padding: 0 $space-050;

    @include tablet() {
      padding: 0;
    }
  }
}

.dark-theme {
  color: $color-white;

  ::v-deep a {
    color: $color-white;
  }

  .link {
    @include outline-focus($color-white);
  }
}
</style>
